import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, coverUrl, downloadPackUrl, packTrackToPlayable, fmtDuration, queueAfter, buyItemFor, detailPathFor } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, Download, ShoppingCart, Package, Layers } from "lucide-react";
import BizumModal from "@/components/BizumModal";
import { GenrePill } from "@/components/GenrePill";
import { useAccessCode } from "@/context/AccessCodeContext";
import { useAuth } from "@/context/AuthContext";
import { ShareButton, sharePackUrl, storyPackUrl, useShareableUrl } from "@/components/ShareButton";
import { PageLoading, LoadError } from "@/components/LoadState";

export default function PackDetail({ config }) {
  const { id } = useParams();
  useShareableUrl("pack", id);
  const [pack, setPack] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [bizumOpen, setBizumOpen] = useState(false);
  const [buying, setBuying] = useState(null);
  const p = usePlayer();
  const ac = useAccessCode();
  const auth = useAuth();

  useEffect(() => {
    setFailed(false);
    api.get(`/packs/${id}`).then((r) => setPack(r.data)).catch((e) => (e?.response?.status === 404 ? setNotFound(true) : setFailed(true)));
  }, [id, attempt]);

  if (failed) return <div className="max-w-7xl mx-auto px-5 py-24"><LoadError onRetry={() => setAttempt((a) => a + 1)} testid="pack-error" /></div>;
  if (notFound) return (
    <div className="max-w-7xl mx-auto px-5 py-24 text-center" data-testid="pack-not-found">
      <h1 className="font-display font-black uppercase text-3xl mb-3">Pack no encontrado</h1>
      <p className="text-white/50 mb-6">Puede que se haya eliminado o movido.</p>
      <Link to="/packs" className="btn-red text-white px-6 py-3 rounded-full font-semibold">Ver packs</Link>
    </div>
  );
  if (!pack) return <div className="max-w-7xl mx-auto px-5 py-24"><PageLoading rows={5} label="Cargando pack…" testid="pack-loading" /></div>;

  const playable = (pack.tracks || []).map((pt) => packTrackToPlayable(pack, pt));
  const isFree = pack.price === 0 || ac.allows("pack", pack.id);
  const currentIdx = playable.findIndex((t) => t.id === p?.current?.id);
  const packPlaying = currentIdx >= 0 && p?.isPlaying;

  const playAll = () => {
    if (!playable.length) return;
    if (currentIdx >= 0) return p.togglePlay();
    p.playTrack(playable[0], playable.slice(1));
  };

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/5" data-testid="pack-hero">
        {pack.cover_path && (
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
            <img src={coverUrl(pack.cover_path, 160)} alt="" className="w-full h-full object-cover scale-125 blur-3xl opacity-60 saturate-150" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-[#0a0a0f]" />
          </div>
        )}
        {!pack.cover_path && <div className="absolute inset-0 ambient-red pointer-events-none" />}
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 grid md:grid-cols-[minmax(0,320px)_1fr] gap-10 items-center">
          <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">
            {pack.cover_path ? (
              <img src={coverUrl(pack.cover_path, 1200)} alt={pack.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-red-900/40 to-black flex items-center justify-center">
                <Package className="w-20 h-20 text-red-600/50" />
              </div>
            )}
          </div>

          <div>
            <div className="font-mono text-xs text-red-500 uppercase tracking-[0.3em] mb-3">
              Pack · {pack.tracks?.length || pack.track_count || "?"} tracks
            </div>
            <h1 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight leading-none text-white" data-testid="pack-detail-title">
              {pack.title}
            </h1>
            {pack.description && <p className="text-white/60 text-base mt-4 max-w-xl whitespace-pre-line">{pack.description}</p>}

            <div className="mt-8 flex flex-wrap gap-3 items-center">
              {playable.length > 0 && (
                <button data-testid="pack-play-all" onClick={playAll}
                  className="btn-red text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2">
                  {packPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  {packPlaying ? "Pausar" : "Reproducir todo"}
                </button>
              )}
              {pack.coming_soon ? (
                <span data-testid={`pack-coming-soon-${pack.id}`} className="px-6 py-3 rounded-full border border-amber-600/50 bg-amber-950/30 text-amber-400 font-semibold font-mono uppercase tracking-widest text-xs flex items-center gap-2">Próximamente · pistas sueltas disponibles</span>
              ) : isFree ? (
                <a href={ac.withCode(downloadPackUrl(pack.id))} data-testid={`pack-download-button-${pack.id}`} onClick={(e) => auth.requireAuth(e)}
                  className="px-6 py-3 rounded-full border border-white/15 hover:border-red-600/60 hover:text-red-500 font-semibold text-white/80 flex items-center gap-2">
                  <Download className="w-4 h-4" /> Descargar pack completo (ZIP)
                </a>
              ) : (
                <button onClick={() => setBizumOpen(true)} data-testid={`pack-buy-button-${pack.id}`}
                  className="px-6 py-3 rounded-full border border-red-600/60 text-red-500 hover:bg-red-600/10 font-semibold flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" /> Comprar pack €{pack.price.toFixed(2)}
                </button>
              )}
              <ShareButton url={sharePackUrl(pack.id)} storyUrl={storyPackUrl(pack.id)} title={pack.title} testid={`pack-share-${pack.id}`} label="Compartir"
                className="px-5 py-3 rounded-full border border-white/15 hover:border-red-600/60 font-semibold text-white/80" />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <div className="font-mono text-xs text-red-500 uppercase tracking-[0.3em] mb-2">Tracklist</div>
            <h2 className="font-display font-black text-3xl uppercase tracking-tight text-white">Pistas del pack</h2>
          </div>
        </div>

        {playable.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-2xl py-16 text-center text-white/40" data-testid="pack-no-tracks">
            Este pack aún no tiene pistas individuales. Descarga el ZIP completo.
          </div>
        ) : (
          <div className="divide-y divide-white/5 border border-white/5 rounded-2xl overflow-hidden bg-[#111116]" data-testid="pack-tracklist">
            {playable.map((t, i) => {
              const isCur = p?.current?.id === t.id;
              const playing = isCur && p?.isPlaying;
              return (
                <div key={t.id} data-testid={`pack-track-row-${t.id}`}
                  onClick={() => p.playTrack(t, queueAfter(playable, t.id))}
                  className={`flex items-center gap-4 px-4 sm:px-6 py-3 transition-colors cursor-pointer select-none ${isCur ? "bg-red-950/20" : "hover:bg-white/[0.03]"}`}>
                  <span className="font-mono text-xs text-white/30 w-6 text-right">{String(i + 1).padStart(2, "0")}</span>
                  <button data-testid={`pack-track-play-${t.id}`}
                    onClick={(e) => { e.stopPropagation(); p.playTrack(t, queueAfter(playable, t.id)); }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${isCur ? "btn-red text-white" : "bg-white/5 hover:bg-red-600/30 text-white"}`}>
                    {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                  </button>
                  <div className="w-10 h-10 rounded overflow-hidden bg-neutral-900 shrink-0 hidden sm:block">
                    {t.cover_path && <img src={coverUrl(t.cover_path, 160)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-semibold truncate ${isCur ? "text-red-400" : "text-white"}`} data-testid={`pack-track-title-${t.id}`}>{t.title}</div>
                    <div className="text-xs text-white/80 truncate flex items-center gap-2" data-testid={`pack-track-artist-${t.id}`}>
                      <span className="truncate">{t.artist}</span>
                      {(t.versions || []).length > 0 && (
                        <Link to={detailPathFor(t)} onClick={(e) => e.stopPropagation()} data-testid={`pack-track-versions-link-${t.id}`} title="Ver todas las versiones"
                          className="inline-flex items-center gap-1 text-pink-400 hover:text-pink-300 shrink-0 font-semibold"><Layers className="w-3 h-3" />{t.versions.length + 1} versiones</Link>
                      )}
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-1.5 shrink-0">
                    {(t.tags || []).map((tag) => (
                      <span key={tag} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border text-white/60 border-white/10">{tag}</span>
                    ))}
                  </div>
                  {t.genre && <GenrePill genre={t.genre} />}
                  {t.bpm && (
                    <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border text-sky-400 border-sky-800/50 bg-sky-950/40 shrink-0" data-testid={`pack-track-bpm-${t.id}`}>
                      {t.bpm} BPM
                    </span>
                  )}
                  {t.duration && (
                    <span className="font-mono text-xs text-white/40 w-10 text-right shrink-0" data-testid={`pack-track-duration-${t.id}`}>{fmtDuration(t.duration)}</span>
                  )}
                  <Link to={detailPathFor(t)} data-testid={`pack-track-section-link-${t.id}`} onClick={(e) => e.stopPropagation()}
                    className="hidden sm:inline font-mono text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-800/60 bg-emerald-950/40 hover:bg-emerald-900/50 hover:text-white px-2 py-0.5 rounded-full shrink-0">
                    Ver tema
                  </Link>
                  {t.price === 0 || ac.allows("pack", pack.id) ? (
                    <>
                    {t.price === 0 && <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border text-emerald-400 border-emerald-800/50 bg-emerald-950/40 shrink-0" data-testid={`pack-track-free-${t.id}`}>Free</span>}
                    <a href={ac.withCode(t.download_url)} data-testid={`pack-track-download-${t.id}`} title="Descargar pista" onClick={(e) => { e.stopPropagation(); auth.requireAuth(e); }}
                      className="p-2 rounded-lg text-white/60 hover:text-red-500 hover:bg-red-600/10 transition-colors">
                      <Download className="w-4 h-4" />
                    </a>
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border text-red-400 border-red-800/50 bg-red-950/40 shrink-0" data-testid={`pack-track-price-${t.id}`}>€{Number(t.price).toFixed(2)}</span>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setBuying(t); }} data-testid={`pack-track-buy-${t.id}`} title="Comprar esta pista"
                        className="p-2 rounded-lg text-red-400 hover:text-white hover:bg-red-600/20 transition-colors"><ShoppingCart className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <BizumModal open={bizumOpen} onClose={() => setBizumOpen(false)} item={pack} itemType="pack" config={config} />
      {buying && <BizumModal open={!!buying} onClose={() => setBuying(null)} {...buyItemFor(buying)} config={config} />}
    </div>
  );
}
